import { createContext, useContext, useEffect, useState } from "react"
import { useFeatureFlag } from "@/hooks/useFeatureFlags"

type Theme = "dark" | "light" | "system"

type ColorVariant = "legacy" | "refined"

type ThemeProviderProps = {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
}

type ThemeProviderState = {
  theme: Theme
  colorVariant: ColorVariant
  setTheme: (theme: Theme) => void
  setColorVariant: (variant: ColorVariant) => void
}

const initialState: ThemeProviderState = {
  theme: "system",
  colorVariant: "legacy",
  setTheme: () => null,
  setColorVariant: () => null,
}

// Helper to check if user prefers reduced motion
const prefersReducedMotion = () => {
  if (typeof window === "undefined") return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

// Local storage key for color variant override (for testing/debugging)
const COLOR_VARIANT_STORAGE_KEY = "meu-controle-financeiro-color-variant"

/**
 * Gets the initial color variant from local storage or returns default
 * This allows manual override for testing purposes
 */
function getInitialColorVariant(): ColorVariant {
  if (typeof window !== "undefined") {
    const stored = localStorage.getItem(COLOR_VARIANT_STORAGE_KEY) as ColorVariant
    if (stored === "legacy" || stored === "refined") {
      return stored
    }
  }
  return "legacy"
}

const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

export function ThemeProvider({
  children,
  defaultTheme = "light",
  storageKey = "meu-controle-financeiro-tema",
  ...props
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => {
    // On mount, read from localStorage or use defaultTheme
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(storageKey) as Theme
      return stored || defaultTheme
    }
    return defaultTheme
  })

  // Color variant state
  const [colorVariant, setColorVariantState] = useState<ColorVariant>(getInitialColorVariant)

  // Fetch color_refined feature flag
  const { enabled: colorRefinedEnabled } = useFeatureFlag("color_refined", {
    staleTime: 10 * 60 * 1000, // 10 minutes - color variant doesn't change often
    refetchOnWindowFocus: false, // Don't refetch on window focus to avoid unnecessary re-renders
  })

  // Update color variant based on feature flag
  useEffect(() => {
    // If there's a manual override in localStorage, use that (for testing/debugging)
    const manualOverride = localStorage.getItem(COLOR_VARIANT_STORAGE_KEY) as ColorVariant | null
    if (manualOverride === "legacy" || manualOverride === "refined") {
      setColorVariantState(manualOverride)
      return
    }

    // Otherwise, use the feature flag result
    // Default to "legacy" (current blue theme) if flag is not enabled
    const variant: ColorVariant = colorRefinedEnabled ? "refined" : "legacy"
    setColorVariantState(variant)
  }, [colorRefinedEnabled])

  useEffect(() => {
    const root = window.document.documentElement

    root.classList.remove("light", "dark")

    if (theme === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
        .matches
        ? "dark"
        : "light"

      root.classList.add(systemTheme)
      return
    }

    root.classList.add(theme)
  }, [theme])

  // Apply data-color-theme attribute to document element
  // This is independent of light/dark theme - it's about the color palette variant
  useEffect(() => {
    if (typeof window === "undefined") return

    const root = window.document.documentElement
    root.setAttribute("data-color-theme", colorVariant)
  }, [colorVariant])

  // Apply theme immediately without transition if reduced motion preferred
  useEffect(() => {
    if (!prefersReducedMotion()) return

    const root = window.document.documentElement

    // Remove transitions when reduced motion is preferred
    root.style.setProperty('transition', 'none')

    // Re-apply current theme
    root.classList.remove("light", "dark")

    if (theme === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
        .matches
        ? "dark"
        : "light"

      root.classList.add(systemTheme)
    } else {
      root.classList.add(theme)
    }

    // Restore transitions after a brief delay
    const timeout = setTimeout(() => {
      root.style.removeProperty('transition')
    }, 50)

    return () => clearTimeout(timeout)
  }, [theme])

  // Listen for system theme changes when theme is set to "system"
  useEffect(() => {
    if (theme !== "system") return

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")

    const handleChange = () => {
      const root = window.document.documentElement
      root.classList.remove("light", "dark")
      root.classList.add(mediaQuery.matches ? "dark" : "light")
    }

    // Modern browsers
    mediaQuery.addEventListener("change", handleChange)
    return () => mediaQuery.removeEventListener("change", handleChange)
  }, [theme])

  const value = {
    theme,
    colorVariant,
    setTheme: (theme: Theme) => {
      // Save to localStorage
      localStorage.setItem(storageKey, theme)
      // Update state
      setThemeState(theme)
    },
    setColorVariant: (variant: ColorVariant) => {
      // Save to localStorage for manual override (testing/debugging)
      localStorage.setItem(COLOR_VARIANT_STORAGE_KEY, variant)
      // Update state
      setColorVariantState(variant)
    },
  }

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext)

  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider")

  return context
}
